import type {
  CreateProjectInput,
  ProjectCatalogQuery,
  ProjectCrafts,
  ProjectDetail,
  ProjectListResponse,
  ProjectPickRow,
  ProjectStructureListResponse,
  SaveProjectCraftsInput,
  UpdateProjectInput,
  UUID,
} from '@qb/shared';
import { http, getToken } from './http';

export const projectsApi = {
  list: (query: ProjectCatalogQuery = {}) => {
    const q = new URLSearchParams();
    if (query.status) q.set('status', query.status);
    if (query.country) q.set('country', query.country);
    if (query.search) q.set('search', query.search);
    const qs = q.toString();
    return http.get<ProjectListResponse>(`/projects${qs ? `?${qs}` : ''}`);
  },
  /** Lightweight rows for the Users "Assign to project" picker. */
  pick: (companyIds: UUID[] = [], search = '') => {
    const q = new URLSearchParams();
    if (companyIds.length) q.set('companyIds', companyIds.join(','));
    if (search.trim()) q.set('search', search.trim());
    const qs = q.toString();
    return http.get<ProjectPickRow[]>(`/projects/pick${qs ? `?${qs}` : ''}`);
  },
  detail: (id: UUID) => http.get<ProjectDetail>(`/projects/${id}`),
  /** Buildings of a project (Structures submodule). */
  structures: (id: UUID) =>
    http.get<ProjectStructureListResponse>(`/projects/${id}/structures`),
  /** Permission keys granted by the project's company's package (the ceiling for
   *  this project's access chain). `keys: null` = no package restriction. */
  packagePermissions: (id: UUID) =>
    http.get<{ keys: string[] | null }>(`/projects/${id}/package-permissions`),
  /** Crafts selected for a project (Crafts submodule) — { selected, available }.
   *  Available is scoped to the project's country + active crafts. */
  crafts: (id: UUID) => http.get<ProjectCrafts>(`/projects/${id}/crafts`),
  /** Replace the project's selected crafts (ordered). */
  saveCrafts: (id: UUID, input: SaveProjectCraftsInput) =>
    http.put<ProjectCrafts>(`/projects/${id}/crafts`, input),
  create: (input: CreateProjectInput) => http.post<ProjectDetail>('/projects', input),
  update: (id: UUID, input: UpdateProjectInput) =>
    http.patch<ProjectDetail>(`/projects/${id}`, input),

  /** Authed logo blob → object URL (caller revokes). */
  async logoObjectUrl(id: UUID): Promise<string> {
    const headers: Record<string, string> = { Accept: 'image/*' };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`/api/projects/${id}/logo`, { headers });
    if (!res.ok) throw new Error(`Failed to load logo (${res.status})`);
    return URL.createObjectURL(await res.blob());
  },
};
